import { InvoiceAdjustmentUpsertDto, initializedInvoiceAdjustmentUpsertDto } from './../dto/invoice-adjustment-upsert.dto';
import { InvoiceUpsertResultDto } from './../dto/invoice-upsert-result.dto';
import { NavigationService } from 'src/app/shared/navigation/navigation.service';
import { HttpErrorResponse } from '@angular/common/http';
import { InvoiceService } from './../../revenue/shared/invoice/invoice.service';
import { CurrencyPipe } from './../../shared/pipes/currency.pipe';
import { InvoiceLineItemUpsertDto } from './../dto/invoice-line-item-upsert.dto';
import { UtilService, NotificationService, TranslationService } from 'src/app/shared';
import { InvoiceUpsertDto, initializedInvoiceUpsertDto } from './../dto/invoice-upsert.dto';
import { Component, OnInit, Input, Output, EventEmitter, OnChanges, SimpleChanges } from '@angular/core';
import { OptionalRevenueItemDto, WC_OptionalRevenueItemAction } from 'src/app/revenue/shared/revenue-items/optional-revenue-item.dto';
import { InvoiceRebalanceDto } from 'src/app/revenue/shared/dto/invoice-rebalance.dto';

@Component({
  selector: 'wl-invoice-modification',
  templateUrl: './invoice-modification.component.html',
  styleUrls: ['./invoice-modification.component.less'],
    standalone: false
})
export class InvoiceModificationComponent implements OnInit, OnChanges {
  @Output() modificationClosed: EventEmitter<number> = new EventEmitter();
  @Output() invoiceModified: EventEmitter<boolean> = new EventEmitter();
  @Output() showPromptChange: EventEmitter<boolean> = new EventEmitter();
  @Input()
  get showPrompt() {
    return this._showPrompt;
  }
  set showPrompt(value: boolean) {
    this._showPrompt = value;
    this.showPromptChange.emit(this._showPrompt);
  }

  @Input() itemName = '';
  @Input() invoice: InvoiceUpsertDto;

  public showModification = false;
  public flatRelatedItems: Array<OptionalRevenueItemDto>;
  public modifiedInvoice: InvoiceUpsertDto;
  public addedLineItems = 0;
  public isSaving = false;

  private _showPrompt = false;
  private _invoiceRebalanceAdjustments: Array<InvoiceAdjustmentUpsertDto> = [];

  constructor(
    private _utilService: UtilService,
    private _invoiceService: InvoiceService,
    private _translationService: TranslationService,
    private _notificationService: NotificationService
  ) {}

  ngOnInit() {
    this.flatRelatedItems = new Array<OptionalRevenueItemDto>();
    this.modifiedInvoice = { InvoiceHeader: {}, LineItems: [] } as InvoiceUpsertDto;
  }

  ngOnChanges(changes: SimpleChanges) {
    if (changes.invoice && this.invoice.LineItems.length > 0) {
      this._invoiceRebalanceAdjustments = [];
      this.modifiedInvoice = this._utilService.clone(this.invoice);

      for (const lineItem of this.modifiedInvoice.LineItems) {
        if (lineItem.RelatedRevenueItems) {
          for (const relatedItem of lineItem.RelatedRevenueItems) {
            if (this.flatRelatedItems.findIndex((x) => x.ID === relatedItem.ID) < 0) {
              if (relatedItem.PercentOfLineItemAmount > 0) {
                relatedItem.ComputedAmount = Math.round(lineItem.Amount * (relatedItem.PercentOfLineItemAmount / 100) * 100) / 100;
              } else {
                relatedItem.ComputedAmount = relatedItem.FlatAmount;
              }
              this.flatRelatedItems.push(relatedItem);

              if (relatedItem.OptOut) {
                this.addRelatedLineItem(lineItem, relatedItem);
              }
            }
          }
        }
      }
    }
  }

  public optInCheckChanged(relatedItem: OptionalRevenueItemDto) {
    const lineItem = this.modifiedInvoice.LineItems.find((x) => x.ItemNum === relatedItem.ItemNum);
    if (relatedItem.OptOut) {
      this.addRelatedLineItem(lineItem, relatedItem);
    } else {
      this.removeRelatedLineItem(lineItem, relatedItem);
    }
  }

  public customAmountChanged(relatedItem: OptionalRevenueItemDto, newAmount) {
    relatedItem.ComputedAmount = newAmount;

    if (relatedItem.OptOut) {
      const lineItem = this.modifiedInvoice.LineItems.find(
        (x) => x.ItemNum === relatedItem.ItemNum_Optional && x.Item === relatedItem.Descr
      );
      const difference = relatedItem.ComputedAmount - lineItem.Amount;
      lineItem.Amount += difference;
      lineItem.UnitPrice = lineItem.Amount;
      this.modifiedInvoice.InvoiceHeader.TotalCost += difference;
      this.modifiedInvoice.AdjustedAmountDue += difference;
      if (relatedItem.Action === WC_OptionalRevenueItemAction.Rebalance) {
        let adjustmentIndex = this._invoiceRebalanceAdjustments.findIndex((x) => x.RelatedLineItemId === relatedItem.ID);
        this._invoiceRebalanceAdjustments[adjustmentIndex].AdjustmentAmount = relatedItem.ComputedAmount * -1;
      }
    }
  }

  public cancel() {
    this.showPrompt = false;
    this.showModification = false;
    this.modifiedInvoice = { InvoiceHeader: {} } as InvoiceUpsertDto;
    this.flatRelatedItems = new Array<OptionalRevenueItemDto>();
    this.modificationClosed.next(this.invoice.InvoiceHeader.InvoiceNum);
  }

  public saveModifications() {
    if (this.addedLineItems > 0) {
      this.isSaving = true;
      this.modifiedInvoice.InvoiceHeader.Dirty = true;
      let invoiceRebalanceDto = {
        RebalanceAdjustments: this._invoiceRebalanceAdjustments,
        ModifiableInvoice: this.modifiedInvoice,
      } as InvoiceRebalanceDto;
      this._invoiceService
        .ApplyRebalanceAdjustments(invoiceRebalanceDto)
        .then((result: InvoiceUpsertResultDto) => {
          if (result.UpsertSuccessful) {
            this.isSaving = false;
            this.modifiedInvoice = { InvoiceHeader: {} } as InvoiceUpsertDto;
            this.flatRelatedItems = new Array<OptionalRevenueItemDto>();
            this.showModification = false;
            this._notificationService.success(this._translationService.getInstant('invoices.invoice-modification.success'));
            this.invoiceModified.emit(true);
            this.modificationClosed.emit(result.InvoiceNum);
          } else {
            for (const message of result.Messages) {
              this._notificationService.error(message);
              this.isSaving = false;
            }
          }
        })
        .catch((error) => {
          this._notificationService.httpError(error);
        });
    }
  }

  private addRelatedLineItem(lineItem: InvoiceLineItemUpsertDto, relatedItem: OptionalRevenueItemDto) {
    const index = this.modifiedInvoice.LineItems.findIndex((x) => x.ItemNum === relatedItem.ItemNum) + 1;
    const newLI = this._utilService.clone(lineItem) as InvoiceLineItemUpsertDto;
    newLI.LineItemId = 0;
    newLI.Item = relatedItem.Descr;
    newLI.Descr = relatedItem.Descr;
    newLI.Amount = relatedItem.ComputedAmount;
    newLI.UnitPrice = newLI.Amount;
    newLI.ItemNum = relatedItem.ItemNum_Optional;
    newLI._isRelatedRevenueItem = true;
    this.unsetCopiedFields(newLI);

    if (!relatedItem.UseLineItemApplyDate) {
      newLI.ApplyDate = new Date();
    }

    this.modifiedInvoice.LineItems.splice(index, 0, newLI);
    this.addedLineItems += 1;

    if (relatedItem.Action === WC_OptionalRevenueItemAction.Rebalance) {
      let rebalanceAdjustment = initializedInvoiceAdjustmentUpsertDto();
      lineItem.AdjustmentAmountToApply -= relatedItem.ComputedAmount;
      rebalanceAdjustment.InvoiceLineItemId = lineItem.LineItemId;
      rebalanceAdjustment.AdjustmentTypeId = relatedItem.RebalanceAdjustmentTypeId;
      rebalanceAdjustment.AdjustmentAmount -= relatedItem.ComputedAmount;
      rebalanceAdjustment.RelatedLineItemId = relatedItem.ID;
      this._invoiceRebalanceAdjustments.push(rebalanceAdjustment);
    } else {
      this.modifiedInvoice.InvoiceHeader.TotalCost += newLI.Amount;
      this.modifiedInvoice.AdjustedAmountDue += newLI.Amount;
    }
  }

  private removeRelatedLineItem(lineItem: InvoiceLineItemUpsertDto, relatedItem: OptionalRevenueItemDto) {
    const index = this.modifiedInvoice.LineItems.findIndex(
      (x) => x.ItemNum === relatedItem.ItemNum_Optional && x.Item === relatedItem.Descr
    );
    const price = relatedItem.ComputedAmount;

    this.modifiedInvoice.LineItems.splice(index, 1);
    this.addedLineItems -= 1;

    if (relatedItem.Action === WC_OptionalRevenueItemAction.Rebalance) {
      lineItem.AdjustmentAmountToApply += price;
      let adjustmentIndex = this._invoiceRebalanceAdjustments.findIndex((x) => x.RelatedLineItemId === relatedItem.ID);
      this._invoiceRebalanceAdjustments.splice(adjustmentIndex, 1);
    } else {
      this.modifiedInvoice.InvoiceHeader.TotalCost -= price;
      this.modifiedInvoice.AdjustedAmountDue -= price;
    }
  }

  private unsetCopiedFields(lineItem: InvoiceLineItemUpsertDto) {
    lineItem.BillingId = null;
    lineItem.Batch_Creation = null;
    lineItem.Posted = false;
    lineItem.DatePosted = null;
    lineItem.PostedBy = null;
    lineItem.Quantity = 1;
    lineItem.BasePrice = null;
    lineItem.FlatFee = null;
    lineItem.Taxable = false;
    lineItem.DateChanged = new Date();
    lineItem.DateCreated = new Date();
    lineItem.ChangedBy = null;
    lineItem.SoldBy = null;
    lineItem.CommissionAmount = null;
    lineItem.CommissionPercent = null;
    lineItem.Batch_Posting = null;
    lineItem.TaxAppliedToLineItemId = null;
    lineItem.HasTaxLineItem = false;
    lineItem.EventCustomRegFieldResponseId = null;
    lineItem.AdjustedAmountDue = null;
    lineItem.AdjustmentAmountToApply = 0;
  }
}
